'use strict';

var jsxRuntime = require('react/jsx-runtime');
var react = require('react');
var hooks_useCombinedRefs = require('../../hooks/useCombinedRefs.js');
var reactDom = require('@floating-ui/react-dom');
var transitions_fade_Fade = require('../../transitions/fade/Fade.js');
var utilities_combineClasses = require('../../_chunks/combineClasses-5P8GTQkg.js');
var components_portal_Portal = require('../portal/Portal.js');
require('react-transition-group');
require('lodash/isNumber');
require('react-dom');
require('../../theme/ThemeProvider.js');
require('../../_chunks/index-CIVOGJRG.js');
require('../../_chunks/designTokens-CKP-YoT2.js');

function getRelatedPlacements(basePlacement) {
    return [
        basePlacement,
        `${basePlacement}-start`,
        `${basePlacement}-end`,
    ];
}
function getOppositeAlignmentPlacement(placement) {
    const hash = { start: 'end', end: 'start' };
    return placement.replaceAll(/start|end/g, (matched) => hash[matched]);
}
function getOppositePlacement(placement) {
    const hash = {
        left: 'right',
        right: 'left',
        bottom: 'top',
        top: 'bottom',
    };
    return placement.replaceAll(/left|right|bottom|top/g, (matched) => hash[matched]);
}
function getOppositeAxisPlacement(placement) {
    const hash = {
        left: [...getRelatedPlacements('bottom'), ...getRelatedPlacements('top')],
        right: [...getRelatedPlacements('bottom'), ...getRelatedPlacements('top')],
        bottom: [...getRelatedPlacements('right'), ...getRelatedPlacements('left')],
        top: [...getRelatedPlacements('right'), ...getRelatedPlacements('left')],
    };
    return hash[placement.split('-').shift()] ?? [placement];
}
function getOrderedFallbackPlacements(placement, oppositeAxisPlacementsEnabled) {
    const oppositePlacement = getOppositePlacement(placement);
    const placements = [
        getOppositeAlignmentPlacement(placement),
        oppositePlacement,
        getOppositeAlignmentPlacement(oppositePlacement),
    ];
    if (oppositeAxisPlacementsEnabled) {
        placements.push(...getOppositeAxisPlacement(placement));
    }
    return placements;
}

const useFloatingElement = ({ arrowNode, placement = 'bottom', strategy = 'fixed', offset: offsetValue = arrowNode ? 8 : 4, flip: flipEnabled = true, shift: shiftEnabled = false, oppositeAxisPlacement: oppositeAxisPlacementEnabled = true, middleware = [], }) => {
    const middlewares = [
        ...middleware,
        reactDom.size({
            apply({ availableWidth, elements }) {
                Object.assign(elements.floating.style, {
                    maxWidth: shiftEnabled || flipEnabled // To prevent content shrinking before shifting/flipping, only apply calculated max-width when these are not enabled
                        ? '100vw'
                        : `${availableWidth}px`,
                });
            },
        }),
    ];
    if (offsetValue > 0)
        middlewares.push(reactDom.offset(offsetValue));
    if (shiftEnabled)
        middlewares.push(reactDom.shift());
    if (flipEnabled)
        middlewares.push(reactDom.flip({
            fallbackPlacements: getOrderedFallbackPlacements(placement, oppositeAxisPlacementEnabled),
        }));
    if (arrowNode)
        middlewares.push(reactDom.arrow({ element: arrowNode }));
    const { x, y, refs, middlewareData, placement: floatingUiPlacement, strategy: floatingUiStrategy, update, } = reactDom.useFloating({
        placement,
        strategy,
        middleware: middlewares,
        whileElementsMounted: reactDom.autoUpdate,
    });
    return {
        styles: getFloatingElementStyles(floatingUiStrategy, x, y),
        arrowStyles: arrowNode
            ? getArrowStyles(middlewareData?.arrow, floatingUiPlacement, 
            // assuming that the arrow node is a 45deg rotated square, we need to offset it with its diagonal
            Math.sqrt((arrowNode.getBoundingClientRect().height / 2) ** 2 / 2))
            : {},
        refs,
        update,
    };
};
const getFloatingElementStyles = (strategy, x, y) => ({
    position: strategy,
    top: y ?? undefined,
    left: x ?? undefined,
});
const getArrowStyles = (arrowData, placement, offset) => {
    // https://floating-ui.com/docs/tutorial#arrow-middleware
    // Handle aligned placements, like 'top-start' rather than only 'top'
    const staticSide = {
        top: 'bottom',
        right: 'left',
        bottom: 'top',
        left: 'right',
    }[placement.split('-')[0]] || 'bottom';
    return {
        left: arrowData?.x != null ? arrowData.x : undefined,
        top: arrowData?.y != null ? arrowData.y : undefined,
        [staticSide]: -offset,
    };
};

require("../../_assets/FloatingContainer-Db-SEkvB.css");var styles = {"arrow":"FloatingContainer__cg_TR-hcB-__arrow","container":"FloatingContainer__cg_TR-hcB-__container","content":"FloatingContainer__cg_TR-hcB-__content"};

const FloatingContainer = react.forwardRef(({ anchorRef, open, portal = false, children, className, style, testId, theme, arrow, offset, shift, oppositeAxisPlacement, flip, placement, strategy, contentProps, ...props }, ref) => {
    const [arrowNode, setArrowNode] = react.useState();
    const { arrowStyles, styles: floatingStyles, refs: { setFloating: setFloatingRef, setReference: setAnchorRef }, update, } = useFloatingElement({
        offset,
        shift,
        flip,
        placement,
        strategy,
        arrowNode,
        oppositeAxisPlacement,
    });
    react.useEffect(() => {
        setAnchorRef(anchorRef.current);
    }, [open, setAnchorRef, anchorRef]);
    const Wrapper = portal ? components_portal_Portal.Portal : react.Fragment;
    return (jsxRuntime.jsx(Wrapper, { children: jsxRuntime.jsxs(transitions_fade_Fade.Fade, { timeout: 100, visible: open, className: utilities_combineClasses.combineClasses(className, styles.container), style: { ...floatingStyles, ...style }, ref: hooks_useCombinedRefs.useCombinedRefs(setFloatingRef, ref), onEntering: 
            // the transition in fade unmounts the children when they are not shown
            // and that causes floating ui to calculate the initial coordinates inaccurately.
            // so we manually trigger an update to the coordinates once the children are rendered out
            () => setTimeout(() => update()), "data-testid": testId, "data-theme": theme, ...props, children: [jsxRuntime.jsx("div", { ...contentProps, className: utilities_combineClasses.combineClasses(styles.content, contentProps?.className), children: children }), arrow && (jsxRuntime.jsx("div", { ref: setArrowNode, style: arrowStyles, className: styles.arrow }))] }) }));
});
FloatingContainer.displayName = 'FloatingContainer';

exports.FloatingContainer = FloatingContainer;
//# sourceMappingURL=FloatingContainer.js.map
